import { useMemo } from "react";
import useWidgetData from "../../../components/widgets/hooks/useWidgetData";

/**
 * useBlockData - thin wrapper around the existing widget data-fetch seam
 * (useWidgetData) for board-report print blocks.
 *
 * Reuses useWidgetData rather than re-implementing fetching/auth/token
 * handling, so print-mode (?print=true&token=...) support comes for free.
 *
 * @param {string} endpoint - API endpoint to fetch
 * @param {Object} [options]
 * @param {string} [options.method='GET']
 * @param {Object} [options.params] - query params
 * @param {Object} [options.body] - request body (POST)
 * @returns {{ data: any, isLoading: boolean, error: string|null }}
 */
const useBlockData = (endpoint, { method = "GET", params, body } = {}) => {
  // Stringify params/body so the memoized config below is stable across
  // renders even when callers pass a fresh object literal each time -
  // react-query relies on a stable dataSource identity for a stable query key.
  const paramsKey = params ? JSON.stringify(params) : "";
  const bodyKey = body ? JSON.stringify(body) : "";

  const config = useMemo(
    () => ({
      id: `block:${method}:${endpoint}:${paramsKey}:${bodyKey}`,
      dataSource: {
        type: "api",
        endpoint,
        method,
        params: paramsKey ? JSON.parse(paramsKey) : undefined,
        body: bodyKey ? JSON.parse(bodyKey) : undefined,
      },
    }),
    [endpoint, method, paramsKey, bodyKey]
  );

  const { data, isLoading, error } = useWidgetData(config, []);

  return { data, isLoading, error };
};

export default useBlockData;
